// Native-mode input reports of the G27 and G29 as WebHID describes them, and
// encoders for them. Used by the tests and by the simulated wheel
// (?fakewheel, ?fakewheel=g29); a real device supplies its own descriptor.
//
// G27 (11 bytes, no report ID): hat, 22 buttons, 14-bit wheel, three 8-bit
// pedal axes, then vendor bytes (raw shifter position and flags, with the
// reverse gate at bit 80).
//
// G29 with its switch on PS3 (12 bytes, no report ID; layout per SDL's lg4ff
// driver and sonik-br/lgff_wheel_adapter): hat, 25 buttons (✕ □ ○ △, R1/L1
// paddles, R2 L2, Share, Options, R3 L3, shifter 1-6, reverse, + − dial
// clockwise/anticlockwise, Enter, PS), 3 padding bits, 16-bit wheel, three
// pedal bytes, then raw shifter X/Y and a flags byte.

const GD = (id: number): number => (0x01 << 16) | id;
const BUTTON = (id: number): number => (0x09 << 16) | id;

function collections(input: HIDReportItem[]): HIDCollectionInfo[] {
  return [
    {
      usagePage: 0x01,
      usage: 0x04,
      type: 1,
      children: [],
      inputReports: [{ reportId: 0, items: input }],
      outputReports: [{ reportId: 0, items: [{ usages: [(0xff00 << 16) | 2], reportSize: 8, reportCount: 7, logicalMinimum: 0, logicalMaximum: 255 }] }],
      featureReports: [],
    },
  ];
}

const HAT: HIDReportItem = { usages: [GD(0x39)], reportSize: 4, reportCount: 1, logicalMinimum: 0, logicalMaximum: 7, hasNull: true };
const PEDALS: HIDReportItem = { usages: [GD(0x31), GD(0x32), GD(0x35)], reportSize: 8, reportCount: 3, logicalMinimum: 0, logicalMaximum: 255 };
const VENDOR: HIDReportItem = { usages: [(0xff00 << 16) | 1], reportSize: 8, reportCount: 3, logicalMinimum: 0, logicalMaximum: 255 };
const buttons = (n: number): HIDReportItem => ({ isRange: true, usageMinimum: BUTTON(1), usageMaximum: BUTTON(n), reportSize: 1, reportCount: n, logicalMinimum: 0, logicalMaximum: 1 });

export const G27_COLLECTIONS: HIDCollectionInfo[] = collections([
  HAT,
  buttons(22),
  { usages: [GD(0x30)], reportSize: 14, reportCount: 1, logicalMinimum: 0, logicalMaximum: 16383 },
  PEDALS,
  VENDOR,
]);

export const G29_COLLECTIONS: HIDCollectionInfo[] = collections([
  HAT,
  buttons(25),
  { reportSize: 1, reportCount: 3 }, // padding
  { usages: [GD(0x30)], reportSize: 16, reportCount: 1, logicalMinimum: 0, logicalMaximum: 65535 },
  PEDALS,
  VENDOR,
]);

export const G27_REPORT_BYTES = 11;
export const G29_REPORT_BYTES = 12;

export interface WheelReportState {
  hat: number;
  buttons: boolean[];
  /** 0..1, 0.5 centred. */
  wheel: number;
  /** Raw 0..1 per pedal axis (Logitech pedals read 1 at rest). */
  pedals: [number, number, number];
}

function writer(bytes: number): { out: Uint8Array; put(value: number, size: number): void } {
  const out = new Uint8Array(bytes);
  let bit = 0;
  return {
    out,
    put(value, size) {
      for (let i = 0; i < size; i++, bit++) if (Math.floor(value / 2 ** i) % 2) out[bit >> 3]! |= 1 << (bit & 7);
    },
  };
}

const unit = (v: number): number => Math.min(1, Math.max(0, v));

/** Encode a G27 input report (inverse of the decoder, for the simulated wheel and tests). */
export function encodeG27Report(state: WheelReportState): Uint8Array {
  const w = writer(G27_REPORT_BYTES);
  w.put(state.hat < 0 ? 8 : state.hat, 4);
  for (let i = 0; i < 22; i++) w.put(state.buttons[i] ? 1 : 0, 1);
  w.put(Math.round(unit(state.wheel) * 16383), 14);
  for (const p of state.pedals) w.put(Math.round(unit(p) * 255), 8);
  // Raw shifter X/Y (unused), then the reverse flag at bit 80.
  w.put(0, 16);
  w.put(state.buttons[22] ? 1 : 0, 1);
  return w.out;
}

/** Encode a G29 input report; reverse is an ordinary button (18) on this wheel. */
export function encodeG29Report(state: WheelReportState): Uint8Array {
  const w = writer(G29_REPORT_BYTES);
  w.put(state.hat < 0 ? 8 : state.hat, 4);
  for (let i = 0; i < 25; i++) w.put(state.buttons[i] ? 1 : 0, 1);
  w.put(0, 3);
  w.put(Math.round(unit(state.wheel) * 65535), 16);
  for (const p of state.pedals) w.put(Math.round(unit(p) * 255), 8);
  return w.out;
}

/** Simulated wheels for ?fakewheel. */
export const FAKE_WHEELS = {
  g27: { productId: 0xc29b, productName: 'G27 Racing Wheel (simulated)', collections: G27_COLLECTIONS, buttons: 23, encode: encodeG27Report },
  g29: { productId: 0xc24f, productName: 'G29 Driving Force Racing Wheel (simulated)', collections: G29_COLLECTIONS, buttons: 25, encode: encodeG29Report },
} as const;

export type FakeWheelModel = keyof typeof FAKE_WHEELS;
